// @flow
import * as React from 'react';
import classnames from 'classnames';
import { themr, type ThemeType } from 'react-css-themr';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import noop from 'lodash/noop';

import DefaultSection from './DefaultSection';
import DefaultSubSection from './DefaultSubSection';

import defaultTheme from './Sidebar.scss';

type Id = { getId: () => string } | string;
type SubSectionType = {
    id: Id,
    disabled?: boolean,
    limitation?: React.Node,
    className?: ?string
};

type SectionType = {
    id: Id,
    isGrouped?: boolean,
    subsections: Array<SubSectionType>,
    className?: ?string,
    disabled?: boolean,
    testId?: string
};

type OwnProps = {
    selectedSectionId: string,
    onChange?: (id: string) => void,
    clientName?: string,
    sections: Array<SectionType>,
    onDragStart?: () => void,
    onDragUpdate?: () => void,
    onDragEnd?: () => void,
    hoveredDragIndex?: number
};

type Props = {
    ...OwnProps,
    theme: ThemeType
};

type Options = {
    Title?: React.AbstractComponent<any>,
    GroupedSection?: React.AbstractComponent<any>,
    UngroupedSection?: React.AbstractComponent<any>,
    SubSection?: React.AbstractComponent<any>,
    Footer?: React.AbstractComponent<any>,
    options?: { draggable: boolean }
};

const sidebarFactory = ({
    Title: TitleSection,
    GroupedSection = DefaultSection,
    UngroupedSection = DefaultSection,
    SubSection = DefaultSubSection,
    Footer,
    options = { draggable: false }
}: Options): React.AbstractComponent<OwnProps> => {
    const { draggable } = options;

    const getId = (id: Id) => {
        return id.getId ? id.getId() : id;
    };

    class Sidebar extends React.PureComponent<Props> {
        renderSections = (
            sections: Array<SectionType>,
            selectedSectionId,
            onChange,
            theme,
            hoveredDragIndex
        ) => {
            return sections.map((section, index) => {
                const { id: sectionId, isGrouped, subsections } = section;
                const id = getId(sectionId);
                const hasSubsections = !!(isGrouped || subsections);
                const isHoveredDuringDrag = hasSubsections
                    ? hoveredDragIndex === id
                    : hoveredDragIndex === index;
                return draggable && !hasSubsections ? (
                    <Draggable
                        draggableId={id}
                        index={index}
                        isDragDisabled={hasSubsections}
                    >
                        {(dragProvided, snapshot) => {
                            const dragProps = {
                                hoveredDragIndex,
                                isDragging: snapshot.isDragging,
                                isHoveredDuringDrag
                            };
                            return (
                                <div>
                                    <div
                                        ref={dragProvided.innerRef}
                                        {...dragProvided.draggableProps}
                                        {...dragProvided.dragHandleProps}
                                    >
                                        {this.renderSection(
                                            section,
                                            selectedSectionId,
                                            onChange,
                                            theme,
                                            dragProps
                                        )}
                                    </div>
                                    {dragProvided.placeholder}
                                </div>
                            );
                        }}
                    </Draggable>
                ) : (
                    this.renderSection(
                        section,
                        selectedSectionId,
                        onChange,
                        theme,
                        { isHoveredDuringDrag }
                    )
                );
            });
        };

        renderSection = (
            source,
            selectedSectionId,
            onChange,
            theme,
            dragProps = {}
        ) => {
            let section = source;
            const {
                isGrouped,
                subsections,
                id,
                className,
                disabled,
                testId
            } = section;

            if (disabled) {
                return null;
            }

            if (source.section) {
                ({ section } = source);
            }
            const sectionId = getId(id);
            const isSubSectionSelected =
                subsections &&
                subsections.findIndex(subsection => {
                    const subsectionId = getId(subsection.id);
                    return subsectionId === selectedSectionId;
                }) > -1;
            const hasSubsections = isGrouped || subsections;
            const SectionComponent = hasSubsections
                ? GroupedSection
                : UngroupedSection;

            return draggable && hasSubsections ? (
                <Droppable droppableId={sectionId}>
                    {dropProvided => (
                        <div
                            className={theme.folderGeometryCard}
                            ref={dropProvided.innerRef}
                            {...dropProvided.droppableProps}
                        >
                            <SectionComponent
                                {...this.props}
                                {...dragProps}
                                key={sectionId}
                                source={section}
                                subsections={subsections}
                                selectable={!section.nonSelectable}
                                isSelected={selectedSectionId === sectionId}
                                isSubSectionSelected={isSubSectionSelected}
                                onClick={onChange}
                                theme={theme}
                                className={className}
                                testId={testId}
                            >
                                {this.renderSubSections(
                                    sectionId,
                                    subsections,
                                    selectedSectionId,
                                    onChange,
                                    theme,
                                    dragProps.hoveredDragIndex
                                )}
                            </SectionComponent>
                            {dropProvided.placeholder}
                        </div>
                    )}
                </Droppable>
            ) : (
                <SectionComponent
                    {...this.props}
                    {...dragProps}
                    key={sectionId}
                    source={section}
                    subsections={subsections}
                    selectable={!section.nonSelectable}
                    isSelected={selectedSectionId === sectionId}
                    isSubSectionSelected={isSubSectionSelected}
                    onClick={onChange}
                    theme={theme}
                    testId={testId}
                >
                    {this.renderSubSections(
                        sectionId,
                        subsections,
                        selectedSectionId,
                        onChange,
                        theme
                    )}
                </SectionComponent>
            );
        };

        renderSubSections = (
            sectionId,
            subsections,
            selectedSectionId,
            onChange,
            theme,
            hoveredDragIndex
        ) => {
            if (subsections) {
                return subsections.map((subsection, index) => {
                    const subsectionId = getId(subsection.id);
                    return draggable ? (
                        <Draggable draggableId={subsectionId} index={index}>
                            {(dragProvided, snapshot) => {
                                const dragProps = {
                                    isDragging: snapshot.isDragging,
                                    isHoveredDuringDrag:
                                        index === hoveredDragIndex
                                };
                                return (
                                    <div>
                                        <div
                                            ref={dragProvided.innerRef}
                                            {...dragProvided.draggableProps}
                                            {...dragProvided.dragHandleProps}
                                        >
                                            {this.renderSubSection(
                                                subsectionId,
                                                subsection,
                                                selectedSectionId,
                                                onChange,
                                                theme,
                                                dragProps
                                            )}
                                        </div>
                                        {dragProvided.placeholder}
                                    </div>
                                );
                            }}
                        </Draggable>
                    ) : (
                        this.renderSubSection(
                            subsectionId,
                            subsection,
                            selectedSectionId,
                            onChange,
                            theme
                        )
                    );
                });
            }
            return [];
        };

        renderSubSection = (
            id,
            subsection,
            selectedSectionId,
            onChange,
            theme,
            dragProps
        ) => {
            const { disabled } = subsection;
            const hasLimitation = !!subsection.limitation;
            return !disabled ? (
                <SubSection
                    {...this.props}
                    {...dragProps}
                    key={id}
                    source={subsection}
                    hasLimitation={hasLimitation}
                    limitationText={subsection.limitation}
                    isSelected={selectedSectionId === id}
                    onClick={onChange}
                    theme={theme}
                    className={subsection.className}
                />
            ) : null;
        };

        renderDrag = (
            sections,
            selectedSectionId,
            onChange,
            theme,
            hoveredDragIndex
        ) => {
            const grouped = sections.filter(
                section => section.isGrouped || section.subsections
            );
            const ungrouped = sections.filter(
                section => !(section.isGrouped || section.subsections)
            );
            return (
                <div>
                    {this.renderSections(
                        grouped,
                        selectedSectionId,
                        onChange,
                        theme,
                        hoveredDragIndex
                    )}
                    <Droppable droppableId="ungrouped">
                        {ungroupedProvided => (
                            <div
                                className={theme.entityList}
                                ref={ungroupedProvided.innerRef}
                                {...ungroupedProvided.droppableProps}
                            >
                                {this.renderSections(
                                    ungrouped,
                                    selectedSectionId,
                                    onChange,
                                    theme,
                                    hoveredDragIndex
                                )}
                                {ungroupedProvided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </div>
            );
        };

        render() {
            const {
                theme,
                clientName,
                sections,
                selectedSectionId,
                onChange,
                onDragStart,
                onDragUpdate,
                onDragEnd,
                hoveredDragIndex
            } = this.props;

            return (
                <div className={theme.sidebar}>
                    <div className={classnames(theme.sidebarHeader)}>
                        {clientName ? (
                            <div className={theme.logo}>{clientName}</div>
                        ) : null}
                        {TitleSection ? (
                            <TitleSection {...this.props} onClick={onChange} />
                        ) : null}
                    </div>
                    <div className={theme.sidebarContent}>
                        {draggable ? (
                            <DragDropContext
                                onDragStart={onDragStart}
                                onDragUpdate={onDragUpdate}
                                onDragEnd={onDragEnd || noop}
                            >
                                {this.renderDrag(
                                    sections,
                                    selectedSectionId,
                                    onChange,
                                    theme,
                                    hoveredDragIndex
                                )}
                            </DragDropContext>
                        ) : (
                            this.renderSections(
                                sections,
                                selectedSectionId,
                                onChange,
                                theme
                            )
                        )}
                    </div>
                    {Footer ? <Footer {...this.props} /> : []}
                </div>
            );
        }
    }
    return themr('Sidebar', defaultTheme)(Sidebar);
};

export default sidebarFactory({});
export type { Options as sidebarFactoryOptions };
export type { OwnProps as SidebarProps };
export { sidebarFactory };
